import React from "react";
import { Container, Box, Typography, Tabs, Tab } from "@mui/material";
import Footer from "../components/Footer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pujavidhi-legal-tabpanel-${index}`}
      aria-labelledby={`pujavidhi-legal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PujavidhiLegal: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}
        >
          Pujavidhi Legal
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={value} onChange={handleChange} centered>
            <Tab label="Terms & Conditions" />
            <Tab label="Privacy Policy" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: July 10, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Acceptance of Terms</Typography>
            <Typography variant="body1">
              By downloading, installing, or using the Pujavidhi application,
              you agree to be bound by these Terms and Conditions. If you do not
              agree to these terms, you must uninstall the application
              immediately.
            </Typography>

            <Typography variant="h5">
              2. Scope of Service and Disclaimers
            </Typography>
            <Typography variant="body1">
              Pujavidhi is an informational tool providing data on the Nepali
              Panchanga, Hindu festivals, and Vedic ritual procedures.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Precision Disclaimer:</strong> While we rely on verified
                traditional sources and established calendars, the Tithi,
                Nakshatra, and Sait timings are provided for spiritual and
                educational guidance only.
              </li>
              <li>
                <strong>Geographic Variance:</strong> Timings are based on
                standard Nepali coordinates; slight variances may occur
                depending on your specific global location.
              </li>
              <li>
                <strong>Professional Consultation:</strong> For life-critical
                events (such as weddings, Bratabandha, or significant business
                openings), we strongly recommend verifying "Saits" with a
                qualified local priest or Jyotishi.
              </li>
            </Box>

            <Typography variant="h5">
              3. Intellectual Property Rights
            </Typography>
            <Typography variant="body1">
              All text, graphics, icons, and software code within this
              application are the exclusive property of the developers. You are
              granted a limited, non-exclusive, non-transferable license to use
              the app for personal, non-commercial purposes.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                You may not: reverse engineer the app, use data scraping tools,
                or redistribute our ritual guides as your own content.
              </li>
            </Box>

            <Typography variant="h5">4. Limitation of Liability</Typography>
            <Typography variant="body1">
              The developers of Pujavidhi shall not be held liable for any
              damages, losses, or spiritual misinterpretations resulting from
              the use or inability to use the information provided in this app.
              The use of the "Search" and "Panchanga" features is at the user's
              own discretion.
            </Typography>

            <Typography variant="h5">5. Changes to These Terms</Typography>
            <Typography variant="body1">
              We reserve the right to update our Privacy Policy and Terms and
              Conditions at any time. We will notify users of any changes by
              updating the "Last Updated" date at the top of this section.
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: July 10, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Introduction</Typography>
            <Typography variant="body1">
              Pujavidhi ("we," "our," or "us") is dedicated to providing a
              secure and respectful environment for exploring Nepali culture and
              Vedic traditions. This Privacy Policy describes how we handle
              information in the Pujavidhi mobile application. We operate this
              app as an Ad-supported service provided at no cost and intended
              for use "AS IS."
            </Typography>

            <Typography variant="h5">
              2. Information Collection and Non-Collection
            </Typography>
            <Typography variant="body1">
              Our primary philosophy is data minimization.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Personal Data:</strong> We <strong>do not</strong>{" "}
                collect any personally identifiable information (PII) such as
                your name, email address, physical address, or phone number.
              </li>
              <li>
                <strong>Account Deletion:</strong> Since no accounts are
                created, there is no personal data to delete.
              </li>
              <li>
                <strong>Automatic Data Collection:</strong> Like most mobile
                applications, our third-party service providers (such as Google
                AdMob and Google Play Services) may automatically collect
                certain non-identifiable information, including device type,
                operating system, and basic usage statistics to improve service
                stability.
              </li>
            </Box>

            <Typography variant="h5">3. Advertising and Analytics</Typography>
            <Typography variant="body1">
              To keep our services free, we display advertisements provided by{" "}
              <strong>Google AdMob</strong>.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                AdMob uses technologies like cookies or unique device
                identifiers to serve ads based on your interests.
              </li>
              <li>
                You can manage your ad preferences or opt-out of personalized
                ads via your Android device settings (
                <strong>
                  Settings &gt; Google &gt; Ads &gt; Opt out of Ads
                  Personalization
                </strong>
                ).
              </li>
              <li>
                For more information, please review the{" "}
                <a
                  href="https://policies.google.com/technologies/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  Google Advertising Privacy Policy
                </a>
                .
              </li>
            </Box>

            <Typography variant="h5">4. Security</Typography>
            <Typography variant="body1">
              We value your trust in using our app. We use industry-standard
              security measures to protect the integrity of the application.
              However, please be aware that no method of transmission over the
              internet or method of electronic storage is 100% secure.
            </Typography>

            <Typography variant="h5">5. Children’s Privacy</Typography>
            <Typography variant="body1">
              Our services do not address anyone under the age of 13. We do not
              knowingly collect personally identifiable information from
              children. If we discover that a child under 13 has provided us
              with personal information (though our app has no such input
              fields), we immediately delete it from our systems.
            </Typography>
          </Box>
        </TabPanel>
      </Container>
      <Footer />
    </Box>
  );
};

export default PujavidhiLegal;
